import React, { useEffect, useRef, useState } from 'react'
import Rectangle from 'blocks.simple/rectangle/rectangle'
import ModalAccept from 'blocks.simple/modal/_accept/modal_accept'
import Icon from 'blocks.simple/icon/icon'
import styles from './editor__templates.jcss'
import translate from 'core/translate'
import { api } from 'core/api/ConnectionManager'
import { cn } from 'ethcss'
import { editor } from '../editor.local.methods'
import LoaderLazy from '../../loader/_lazy/loader__lazy'
import InfiniteScroll from 'react-infinite-scroller'
import Checkbox from '../../../blocks.simple/checkbox/checkbox'
import Dropdown from '../../../blocks.simple/dropdown/dropdown'
import { colors } from '../../../theme'
import Button from '../../../blocks.simple/button/button'

type Template = {
    id: number
    areas: {
        left: number
        top: number
        width: number
        height: number
    }[]
    linkedQ: boolean
}

const EditorTemplates = ({ acceptedSelectedTemplate }: { acceptedSelectedTemplate: { id: number } }) => {
    const [templates, setTemplates] = useState<Template[]>([])
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
    const [nextPageToken, setNextPageToken] = useState<string | null>(null)
    const [selectedItems, setSelectedItems] = useState<Template[]>([])
    const [templatesForDelete, setTemplateForDelete] = useState<number[]>([])
    const [filter, setFilter] = useState<string>('allTemplates')
    const [init, setInit] = useState<boolean>(false)
    const scrollRef = useRef<HTMLDivElement>(null)

    const deleteTemplate = () => {
        api.send('deleteTemplate', { ids: templatesForDelete }).then((res: any) => {
            if (res && res.ids && res.ids.length) {
                setTemplates(templates.filter((template) => !res.ids.includes(template.id)))
                setTemplateForDelete([])
                setSelectedItems([])
            }
        })
    }

    const getTemplates = () => {
        if (init && !nextPageToken) {
            return
        }

        const data: {
            nextPageToken?: string
            limit?: number
            filter?: string[]
        } = {}

        if (nextPageToken) {
            data.nextPageToken = nextPageToken
        }
        if (!nextPageToken) {
            data.limit = 30
        }
        if (filter !== 'allTemplates') {
            data.filter = [filter]
        }

        api.send<object, { templates: Template[]; nextPageToken: string | null }>('getTemplates', data).then((res) => {
            setTemplates(!init ? res.templates : [...templates, ...res.templates])
            setNextPageToken(res.nextPageToken)

            if (!init) {
                setInit(true)
            }
        })
    }

    const onScrollList = (e: any) => {
        if (scrollRef.current) {
            const scrollValue = e.target.scrollHeight - e.target.scrollTop

            if (scrollValue - 200 <= scrollRef.current.offsetHeight) {
                getTemplates()
            }
        }
    }

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'templatesDeleted',
            (res: { ids: number[] }) => {
                if (res && res.ids && res.ids.length) {
                    setTemplates(templates.filter((template) => !res.ids.includes(template.id)))
                    setTemplateForDelete([])
                }
            },
            listenersId
        )

        return () => {
            listenersId.forEach((id) => api.removeObserver(id))
        }
    }, [templates.length])

    useEffect(() => {
        if (!init) {
            getTemplates()
        }
    }, [init])

    useEffect(() => {
        setTemplates([])
        setInit(false)
    }, [filter])

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.addEventListener('scroll', onScrollList)
        }

        return () => {
            if (scrollRef.current) {
                scrollRef.current.removeEventListener('scroll', onScrollList)
            }
        }
    }, [templates.length, scrollRef.current, nextPageToken, init])

    return (
        <div className={styles.templatesContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <Dropdown
                        mod={'withBorderV'}
                        options={[
                            { id: 'allTemplates', name: translate('allTemplates') },
                            { id: 'used', name: translate('usedTemplates') },
                            { id: 'unused', name: translate('unusedTemplates') },
                        ]}
                        placeholder={translate('choice')}
                        value={filter}
                        rounded={'none'}
                        onChange={(selected) => {
                            setNextPageToken(null)
                            setFilter(selected.id)
                        }}
                        disableChangeRounded={true}
                        buttonClassName={styles.dropdownFilter}
                        textMobPadding={true}
                    />
                </div>
                <div className={styles.headerRight}>
                    <Dropdown
                        mod={'withBorderV'}
                        options={[
                            { id: 'selectAll', name: translate('selectAll') },
                            { id: 'deselect', name: translate('deselect') },
                        ]}
                        placeholder={translate('choice')}
                        value={''}
                        icon={'choice'}
                        iconColor={colors.grey}
                        iconActiveColor={colors.blueAccent}
                        inheritIconColor={true}
                        iconSize={14}
                        rounded={'none'}
                        onChange={(selected) => {
                            setSelectedItems(
                                selected.id === 'selectAll' ? templates.filter((template) => !template.linkedQ) : []
                            )
                        }}
                        disableChangeRounded={true}
                        buttonClassName={styles.dropdownBtn}
                        textMobPadding={true}
                        containerClassName={styles.fullWidth}
                    />
                    <div className={styles.deleteAll}>
                        <Icon
                            disabled={!selectedItems.length}
                            type={'delete'}
                            color={colors.grey}
                            size={18}
                            tooltip={{ title: translate('deleteSelected') }}
                            onClick={(e) => {
                                setTemplateForDelete(selectedItems.map((item) => item.id))
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.scrollable} ref={scrollRef}>
                <div className={styles.wrapper}>
                    {templates.map((template, index) => {
                        const selected = selectedItems.some((item) => item.id === template.id)

                        return (
                            <div key={`editor__template_${index}`} className={styles.cell}>
                                <div className={styles.cellInner}>
                                    <div
                                        className={styles.cellRect}
                                        onClick={() => {
                                            // @ts-ignore
                                            editor.selectArea(null)
                                            // @ts-ignore
                                            if (editor.pageContentLength()) {
                                                setSelectedTemplate(template)
                                            } else {
                                                // @ts-ignore
                                                editor.setArea(template)
                                            }
                                        }}
                                    >
                                        <Rectangle width={16} height={9}>
                                            {template.areas.map((area, areaIndex) => (
                                                <div
                                                    key={`editor__template_area_${index}_${areaIndex}`}
                                                    className={styles.area}
                                                    style={{
                                                        top: `${area.top * 100}%`,
                                                        left: `${area.left * 100}%`,
                                                        width: `${area.width * 100}%`,
                                                        height: `${area.height * 100}%`,
                                                    }}
                                                />
                                            ))}
                                        </Rectangle>
                                    </div>
                                    <div className={styles.title}>
                                        {translate('template')} {index + 1}
                                    </div>
                                    {!template.linkedQ && (
                                        <>
                                            <div className={cn(styles.delete)}>
                                                <Icon
                                                    disabled={
                                                        acceptedSelectedTemplate &&
                                                        acceptedSelectedTemplate.id === template.id
                                                    }
                                                    type={'delete'}
                                                    color={'white'}
                                                    size={17}
                                                    tooltip={{ title: translate('deleteTemplate') }}
                                                    onClick={(e) => {
                                                        setTemplateForDelete([template.id])
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className={styles.checkWrapper}
                                                style={selected ? { opacity: 1 } : {}}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Checkbox
                                                    mod={'circleDis'}
                                                    onClick={() => {
                                                        if (selected) {
                                                            setSelectedItems(
                                                                selectedItems.filter((item) => item.id !== template.id)
                                                            )

                                                            return
                                                        }

                                                        setSelectedItems([...selectedItems, template])
                                                    }}
                                                    checked={selected}
                                                />
                                            </div>
                                        </>
                                    )}
                                    {template.linkedQ && (
                                        <div className={cn(styles.linked)}>
                                            <Icon
                                                type={'linked'}
                                                color={'white'}
                                                size={24}
                                                tooltip={{ title: translate('templateInUse') }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
                <ModalAccept
                    open={!!selectedTemplate}
                    onReject={() => {
                        setSelectedTemplate(null)
                    }}
                    onAccept={() => {
                        // @ts-ignore
                        editor.setArea(selectedTemplate)
                        setSelectedTemplate(null)
                    }}
                    title={translate('templateWarningTitle')}
                    description={translate('templateWarningDescription')}
                    rejectLabel={translate('cancel')}
                    acceptLabel={translate('change')}
                    onClose={() => {
                        setSelectedTemplate(null)
                    }}
                />
            </div>
            {!!templatesForDelete.length && (
                <div className={styles.deleteModal}>
                    <div className={styles.deleteModalInner}>
                        <div className={styles.deleteModalTitle}>{translate('deleteConfirmTitle')}</div>
                        <div className={styles.deleteModalText}>{translate('deleteTemplatesText')}</div>
                        <div className={styles.controls}>
                            <Button
                                mod={'withBorder'}
                                className={styles.button}
                                onClick={() => setTemplateForDelete([])}
                            >
                                {translate('cancel')}
                            </Button>
                            <Button mod={'fill'} className={styles.button} onClick={deleteTemplate}>
                                {translate('delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default EditorTemplates
